import React, { useMemo, useCallback } from "react";
import { Minus, Plus, Utensils } from "lucide-react";
import { useLang } from "@/contexts/LangContext";
import { MENU_CATEGORIES } from "@/data/menu";

const StepperRow = ({ label, sublabel, desc, qty, onInc, onDec, testKey }) => {
  return (
    <div
      className="flex items-start gap-4 py-3 border-b border-border/50 last:border-b-0"
      data-testid={`menu-row-${testKey}`}
    >
      <div className="min-w-0 flex-1">
        <p className="font-serif-display text-lg leading-tight">
          {label}
          {sublabel && (
            <span className="text-foreground/55 italic"> · {sublabel}</span>
          )}
        </p>
        {desc && (
          <p className="mt-1 text-[13px] text-foreground/60 leading-relaxed">
            {desc}
          </p>
        )}
      </div>
      <div className="shrink-0 flex items-center gap-2">
        <button
          type="button"
          onClick={onDec}
          disabled={qty <= 0}
          aria-label="Remove one"
          data-testid={`menu-dec-${testKey}`}
          className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-border text-foreground/70 hover:text-foreground hover:border-foreground/60 disabled:opacity-30 disabled:cursor-not-allowed transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          <Minus className="w-4 h-4" aria-hidden="true" />
        </button>
        <span
          className="min-w-[26px] text-center font-serif-display text-xl tabular-nums"
          data-testid={`menu-qty-${testKey}`}
          aria-live="polite"
        >
          {qty}
        </span>
        <button
          type="button"
          onClick={onInc}
          aria-label="Add one"
          data-testid={`menu-inc-${testKey}`}
          className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-foreground text-background hover:bg-foreground/85 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          <Plus className="w-4 h-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

/**
 * Menu preorder embedded in the RSVP form.
 * Props:
 *   counts:   { [key]: number }  — key = itemId OR `${itemId}::variant`
 *   onChange: (nextCounts) => void
 */
export const MenuPreorder = ({ counts, onChange }) => {
  const { t } = useLang();

  const setQty = useCallback(
    (key, delta) => {
      const next = { ...counts };
      const q = Math.max(0, (Number(next[key]) || 0) + delta);
      if (q === 0) delete next[key];
      else next[key] = q;
      onChange(next);
    },
    [counts, onChange],
  );

  const totalItems = useMemo(
    () => Object.values(counts).reduce((s, n) => s + (Number(n) || 0), 0),
    [counts],
  );

  const totalLabel =
    totalItems === 1 ? t("rsvp.menu.totalOne") : t("rsvp.menu.totalMany");

  return (
    <div className="space-y-6" data-testid="menu-preorder">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <p className="editorial-kicker">{t("rsvp.menu.overline")}</p>
          <h3 className="editorial-h2 mt-3 text-2xl sm:text-3xl max-w-[26ch]">
            {t("rsvp.menu.title")}
          </h3>
          <p className="mt-3 text-foreground/70 leading-relaxed max-w-[60ch]">
            {t("rsvp.menu.intro")}
          </p>
        </div>
        <div
          className="shrink-0 flex items-center gap-3 text-[12px] tracking-[0.18em] uppercase text-foreground/70"
          data-testid="menu-total-block"
        >
          <span
            className="font-serif-display text-2xl tabular-nums text-foreground"
            data-testid="menu-total-count"
          >
            {totalItems}
          </span>
          {totalItems > 0 ? totalLabel : t("rsvp.menu.empty")}
          {totalItems > 0 && (
            <button
              type="button"
              onClick={() => onChange({})}
              className="ml-3 underline underline-offset-4 hover:text-foreground"
              data-testid="menu-clear-button"
            >
              {t("rsvp.menu.clear")}
            </button>
          )}
        </div>
      </div>

      <div className="space-y-6">
        {MENU_CATEGORIES.map((cat) => (
          <section
            key={cat.id}
            className="surface-card p-5 sm:p-6"
            data-testid={`menu-cat-${cat.id}`}
          >
            <header className="flex items-center gap-2 mb-3">
              <span className="text-2xl" aria-hidden="true">
                {cat.emoji}
              </span>
              <h4 className="font-serif-display italic text-2xl">{cat.label}</h4>
            </header>
            <div>
              {cat.items.map((item) => {
                if (item.variants && item.variants.length > 0) {
                  return item.variants.map((v) => {
                    const key = `${item.id}::${v}`;
                    return (
                      <StepperRow
                        key={key}
                        testKey={key.replace(/[^a-z0-9-]+/gi, "-")}
                        label={item.name}
                        sublabel={v}
                        desc={item.desc}
                        qty={counts[key] || 0}
                        onInc={() => setQty(key, 1)}
                        onDec={() => setQty(key, -1)}
                      />
                    );
                  });
                }
                const key = item.id;
                return (
                  <StepperRow
                    key={key}
                    testKey={key}
                    label={item.name}
                    desc={item.desc}
                    qty={counts[key] || 0}
                    onInc={() => setQty(key, 1)}
                    onDec={() => setQty(key, -1)}
                  />
                );
              })}
            </div>
          </section>
        ))}
      </div>

      {totalItems > 0 && (
        <div
          className="surface-card p-5 flex items-start gap-3"
          data-testid="menu-summary-block"
        >
          <Utensils className="w-5 h-5 shrink-0 text-accent mt-1" aria-hidden="true" />
          <div className="min-w-0 flex-1">
            <p className="editorial-kicker text-accent">
              {totalItems} {totalLabel}
            </p>
            <p className="mt-2 text-foreground/80 text-sm leading-relaxed">
              {Object.entries(counts)
                .filter(([, n]) => Number(n) > 0)
                .map(([key, n]) => {
                  const [id, variant] = key.split("::");
                  const item = MENU_CATEGORIES.flatMap((c) => c.items).find(
                    (i) => i.id === id,
                  );
                  const name = item ? item.name : id;
                  return `${n}× ${name}${variant ? ` · ${variant}` : ""}`;
                })
                .join(", ")}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default MenuPreorder;
